/* eslint-disable react-hooks/exhaustive-deps */
import { useEffect, useRef, useState } from "react";
import { Link, Navigate } from "react-router-dom";
import { ArrowLeft, Upload, Trash2, Loader2, Check } from "lucide-react";
import { toast } from "sonner";
import { api } from "../lib/apiClient";
import { useMatch } from "../lib/MatchContext";
import RequireMatch from "../components/RequireMatch";
import { fileUrl, uploadFile, errMsg } from "../lib/reportApi";

const ASSETS = [
  ["cover_visual", "Cover page key visual", "Background of the cover page"],
  ["inner_visual", "Inner slide key visual", "Background of every department page"],
  ["association_logo", "Association / organiser logo", "PNG with transparency works best"],
  ["match_logo", "Match logo", "Shown beside the association logo"],
];

function AssetSlot({ kind, label, hint, path, onUpload, onRemove }) {
  const ref = useRef(null);
  const [busy, setBusy] = useState(false);
  const send = async (f) => { if (!f) return; setBusy(true); try { await onUpload(kind, f); } finally { setBusy(false); } };
  return (
    <div className="mc-panel p-3" data-testid={`report-asset-${kind}`}
      onDragOver={(e) => e.preventDefault()} onDrop={(e) => { e.preventDefault(); send(e.dataTransfer?.files?.[0]); }}>
      <input ref={ref} type="file" accept="image/*,.heic,.heif" className="hidden" onChange={(e) => { send(e.target.files?.[0]); e.target.value = ""; }} data-testid={`report-asset-input-${kind}`} />
      <div className="mc-label">{label}</div>
      <div className="text-[11px] text-zinc-500 mb-2">{hint}</div>
      <div className="aspect-video bg-zinc-100 flex items-center justify-center overflow-hidden mb-2">
        {path ? <img src={fileUrl(path)} alt="" className="max-w-full max-h-full object-contain" /> : <span className="text-xs text-zinc-400">Drop image or click upload</span>}
      </div>
      <div className="flex gap-2">
        <button type="button" onClick={() => ref.current?.click()} disabled={busy} className="border border-cmdBorder px-2 py-1 text-[11px] uppercase tracking-widest flex items-center gap-1" data-testid={`report-asset-upload-${kind}`}>
          {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : <Upload className="w-3 h-3" />} {path ? "Replace" : "Upload"}
        </button>
        {path && <button type="button" onClick={() => onRemove(kind)} className="border border-cmdBorder hover:text-incidentRed px-2 py-1 text-[11px] uppercase tracking-widest flex items-center gap-1" data-testid={`report-asset-remove-${kind}`}><Trash2 className="w-3 h-3" /> Remove</button>}
      </div>
    </div>
  );
}

const Field = ({ label, children }) => (<label className="flex flex-col gap-1"><span className="mc-label">{label}</span>{children}</label>);
const sel = "border border-cmdBorder p-2 text-sm bg-white";

function Choice({ k, s, set, opts }) {
  return (
    <select className={sel} value={s[k]} onChange={(e) => set({ [k]: e.target.value })} data-testid={`report-setting-${k}`}>
      {opts.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
    </select>
  );
}
function Toggle({ k, s, set, label }) {
  return (
    <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={!!s[k]} onChange={(e) => set({ [k]: e.target.checked })} data-testid={`report-setting-${k}`} /> {label}</label>
  );
}
function Color({ k, s, set, label }) {
  return (
    <Field label={label}>
      <div className="flex items-center gap-2"><input type="color" value={s[k]} onChange={(e) => set({ [k]: e.target.value })} className="w-10 h-9 border border-cmdBorder" data-testid={`report-setting-${k}`} /><span className="font-mono-live text-xs">{s[k]}</span></div>
    </Field>
  );
}

function SettingsForm({ s, set }) {
  return (
    <div className="mc-panel p-4 grid grid-cols-1 sm:grid-cols-2 gap-4" data-testid="report-settings-form">
      <Field label="Report title"><input className={sel} value={s.report_title} onChange={(e) => set({ report_title: e.target.value })} data-testid="report-setting-report_title" /></Field>
      <Field label="Font style"><Choice k="font" s={s} set={set} opts={[["sans", "Sans (Helvetica / Arial)"], ["serif", "Serif (Times)"], ["mono", "Mono (Courier)"]]} /></Field>
      <Color k="primary_color" s={s} set={set} label="Primary colour (header band)" />
      <Color k="accent_color" s={s} set={set} label="Accent colour" />
      <Color k="heading_color" s={s} set={set} label="Heading colour" />
      <Color k="text_color" s={s} set={set} label="Body text colour" />
      <Color k="cover_text_color" s={s} set={set} label="Cover text colour" />
      <Field label="Cover text position"><Choice k="cover_text_position" s={s} set={set} opts={[["left", "Middle left"], ["center", "Centre"], ["bottom-left", "Bottom left"]]} /></Field>
      <Field label="Department heading position"><Choice k="heading_position" s={s} set={set} opts={[["left", "Left"], ["center", "Centre"], ["right", "Right"]]} /></Field>
      <Field label="Header style"><Choice k="header_style" s={s} set={set} opts={[["band", "Coloured band"], ["plain", "Plain (over key visual)"]]} /></Field>
      <Field label="Background fit"><Choice k="bg_fit" s={s} set={set} opts={[["cover", "Fill page (crop edges)"], ["contain", "Show whole image"]]} /></Field>
      <Field label="Background position"><Choice k="bg_anchor" s={s} set={set} opts={[["center", "Centre"], ["top", "Top"], ["bottom", "Bottom"]]} /></Field>
      <Field label="Page number position"><Choice k="page_number_position" s={s} set={set} opts={[["right", "Right"], ["center", "Centre"], ["left", "Left"]]} /></Field>
      <Field label="Page format"><select className={sel} value="16:9" disabled data-testid="report-setting-page_format"><option value="16:9">16:9 Landscape</option></select></Field>
      <Field label="Footer text (optional)"><input className={sel} value={s.footer_text} placeholder="Defaults to report title · match" onChange={(e) => set({ footer_text: e.target.value })} data-testid="report-setting-footer_text" /></Field>
      <Field label="Closing message"><input className={sel} value={s.closing_message} onChange={(e) => set({ closing_message: e.target.value })} data-testid="report-setting-closing_message" /></Field>
      <div className="sm:col-span-2 grid grid-cols-1 sm:grid-cols-2 gap-2 pt-2 border-t border-cmdBorder">
        <Toggle k="show_header" s={s} set={set} label="Show header on inner pages" />
        <Toggle k="show_footer" s={s} set={set} label="Show footer" />
        <Toggle k="page_numbers" s={s} set={set} label="Show page numbers" />
        <Toggle k="cover_overlay" s={s} set={set} label="Dark panel behind cover text" />
        <Toggle k="closing_enabled" s={s} set={set} label="Include closing page" />
        <Toggle k="include_empty_departments" s={s} set={set} label="Include departments with no items" />
        <Toggle k="include_unapproved" s={s} set={set} label="Override: include items not yet approved" />
      </div>
    </div>
  );
}

export default function ReportDesignPage() {
  const { selectedMatchId: M } = useMatch();
  const [s, setS] = useState(null);
  const [canManage, setCanManage] = useState(true);
  const [prev, setPrev] = useState(null);
  const [saving, setSaving] = useState("saved");
  const [loadingPrev, setLoadingPrev] = useState(false);
  const t = useRef(null);

  const refreshPreview = async (settings) => {
    setLoadingPrev(true);
    try { const { data } = await api.post(`/matches/${M}/report/design-preview`, { settings }); setPrev(data); } finally { setLoadingPrev(false); }
  };
  useEffect(() => {
    if (!M) return;
    api.get(`/matches/${M}/report`).then(({ data }) => { setCanManage(data.me.can_manage); setS(data.settings); if (data.me.can_manage) refreshPreview(data.settings); });
  }, [M]);

  const set = (patch) => {
    const next = { ...s, ...patch };
    setS(next); setSaving("pending");
    clearTimeout(t.current);
    t.current = setTimeout(async () => {
      try { await api.put(`/matches/${M}/report/settings`, next); setSaving("saved"); refreshPreview(next); }
      catch (e) { setSaving("error"); toast.error(errMsg(e)); }
    }, 600);
  };
  const upload = async (kind, f) => {
    try { const { data } = await uploadFile(`/matches/${M}/report/assets/${kind}`, f); setS(data); refreshPreview(data); }
    catch (e) { toast.error(errMsg(e, "Upload failed")); }
  };
  const remove = async (kind) => { const { data } = await api.delete(`/matches/${M}/report/assets/${kind}`); setS(data); refreshPreview(data); };

  if (!M) return <RequireMatch />;
  if (!canManage) return <Navigate to="/post-event-report" replace />;  if (!s) return <div className="p-8 text-zinc-500 font-mono-live">loading…</div>;
  return (
    <div className="p-6 md:p-10 pb-24" data-testid="page-report-design">
      <Link to="/post-event-report" className="text-xs uppercase tracking-widest text-zinc-500 flex items-center gap-1 mb-4"><ArrowLeft className="w-3 h-3" /> Post Event Report</Link>
      <div className="flex items-end justify-between gap-3 mb-6 border-b border-cmdBorder pb-6">
        <div>
          <div className="mc-label">Match-specific branding</div>
          <h1 className="font-head font-black text-4xl md:text-5xl uppercase tracking-tight leading-none mt-2">Report Design</h1>
        </div>
        <span className="text-xs font-mono-live text-zinc-500 flex items-center gap-1" data-testid="report-design-save-state">
          {saving === "saved" ? <><Check className="w-3 h-3 text-readyGreen" /> saved</> : saving === "error" ? "not saved" : "saving…"}
        </span>
      </div>
      <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
        <div className="space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {ASSETS.map(([k, l, h]) => <AssetSlot key={k} kind={k} label={l} hint={h} path={s[k]} onUpload={upload} onRemove={remove} />)}
          </div>
          <SettingsForm s={s} set={set} />
        </div>
        <div className="space-y-4 xl:sticky xl:top-4 self-start" data-testid="report-design-preview">
          <div className="mc-label flex items-center gap-2">Live preview {loadingPrev && <Loader2 className="w-3 h-3 animate-spin" />}</div>
          {prev ? (
            <>
              <div><div className="text-xs text-zinc-500 mb-1">Cover page</div><img src={prev.cover} alt="Cover preview" className="w-full aspect-video border border-cmdBorder" data-testid="report-design-preview-cover" /></div>
              <div><div className="text-xs text-zinc-500 mb-1">Inner page (sample, 4 per page)</div><img src={prev.inner} alt="Inner preview" className="w-full aspect-video border border-cmdBorder" data-testid="report-design-preview-inner" /></div>
            </>
          ) : <div className="aspect-video mc-panel flex items-center justify-center"><Loader2 className="w-5 h-5 animate-spin" /></div>}
        </div>
      </div>
    </div>
  );
}
